import {zodResolver} from "@hookform/resolvers/zod";
import {ArrowLeft, CheckCircle, Loader2} from "lucide-react";
import {useMemo, useState} from "react";
import {useForm} from "react-hook-form";
import {Link} from "react-router-dom";
import {useTranslation} from "react-i18next";

import {Button} from "@/components/ui/button";
import {Form, FormControl, FormField, FormItem, FormLabel} from "@/components/ui/form";
import FormError from "@/components/ui/form-error";
import {Input} from "@/components/ui/input";
import {cn} from "@/lib/utils";
import {PublicPageShell} from "@/src/modules/core/components";
import {LOGO_URL_DESKTOP} from "@/src/modules/core/utils/constants";

import {useForgot} from "../hooks";
import {createForgotPasswordSchema, type ForgotPasswordFormValues} from "../schemas";

export default function ForgotPage() {
  const {t} = useTranslation("auth");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const {mutateAsync: forgot, isPending} = useForgot();
  const forgotPasswordSchema = useMemo(() => createForgotPasswordSchema(t), [t]);
  const form = useForm<ForgotPasswordFormValues>({
    defaultValues: {
      email: "",
    },
    resolver: zodResolver(forgotPasswordSchema),
  });

  const handleSubmit = (data: ForgotPasswordFormValues) => {
    try {
      setIsSubmitted(true);

      forgot(data);
    } catch (error) {
      console.log(error);
    }
  };

  const {
    watch,
    formState: {isSubmitting},
  } = form;

  const email = watch("email");

  return (
    <PublicPageShell>
      <div className="rounded-lg bg-background p-6 shadow-lg sm:p-8">
        <div className="mb-8 flex flex-col items-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full">
            <img src={LOGO_URL_DESKTOP} alt="Image" height="100" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">{t("brand.name")}</h1>
          <p className="text-muted-foreground">{t("brand.tagline")}</p>
        </div>

        {!isSubmitted ? (
          <>
            <h2 className="mb-2 text-center text-xl font-semibold text-foreground">
              {t("forgot.title")}
            </h2>
            <p className="mb-6 text-center text-muted-foreground">{t("forgot.description")}</p>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({field, formState}) => (
                    <FormItem>
                      <FormLabel>{t("forgot.email")}</FormLabel>
                      <FormControl>
                        <Input
                          id="email"
                          type="email"
                          placeholder={t("forgot.email_placeholder")}
                          className="border-border"
                          disabled={isPending}
                          {...field}
                        />
                      </FormControl>
                      {formState.errors.email ? (
                        <FormError error={formState.errors.email.message} />
                      ) : null}
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  className={cn(
                    "w-full bg-primary hover:bg-primary/90",
                    isPending && "cursor-not-allowed opacity-80",
                  )}
                  disabled={isPending || isSubmitting}
                >
                  {isPending || isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 size-4 animate-spin" />
                      {t("forgot.sending")}
                    </>
                  ) : (
                    t("forgot.submit")
                  )}
                </Button>
              </form>
            </Form>
          </>
        ) : (
          <div className="py-4 text-center">
            <div className="mb-4 flex justify-center">
              <CheckCircle className="h-16 w-16 text-primary" />
            </div>
            <h2 className="mb-2 text-xl font-semibold text-foreground">{t("forgot.check_email")}</h2>
            <p className="mb-6 text-muted-foreground">
              {t("forgot.sent_to")} <span className="font-medium">{email}</span>
            </p>
            <p className="mb-4 text-sm text-muted-foreground">{t("forgot.spam_hint")}</p>
            <Button
              variant="outline"
              className="border-border"
              onClick={() => {
                setIsSubmitted(false);
              }}
            >
              {t("forgot.try_again")}
            </Button>
          </div>
        )}

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="inline-flex items-center text-sm font-medium text-primary hover:text-primary/90"
          >
            <ArrowLeft className="mr-2 size-4" />
            {t("forgot.back_to_login")}
          </Link>
        </div>
      </div>

      <div className="mt-6 text-center text-xs text-muted-foreground">
        {t("brand.copyright", {year: new Date().getFullYear()})}
      </div>
    </PublicPageShell>
  );
}
